/**
 * 学习乐园 · 五子棋（game-wuziqi.js）
 * ============================================================
 * 产品底线：电脑每走一步，必须用大白话说清「为什么」——理由不是
 * 事后编的，而是把引擎真实的评分构成（成五/活四/冲四/活三/眠三/活二）
 * 翻成一句话，解释和着法出自同一份数据。
 *
 * 架构（与琴谱跟弹同口径）：零 vendor、零引擎产物、零请求。
 * 评分与搜索内置在本文件：候选点打分（攻+防），进阶/挑战档再做
 * 一层「对手最佳应手」展望。无禁手自由规则（长连也算五连）。
 *
 * 冒烟钩子：window.STWuziqi 暴露纯引擎函数（bestMove / checkWin /
 * explain / evalPoint / candidateMoves / dirInfo / emptyBoard），
 * 供 smoke-study.mjs 跑确定性用例（不经过 DOM）。
 */
(function (global) {
  'use strict';

  var N = 15;
  var FILES = 'ABCDEFGHIJKLMNO';    // 列名（含 I，不跳过）
  var STORE_KEY = 'study-chess-wuziqi';

  /* ============ 引擎：棋盘基础 ============ */

  function emptyBoard() {
    var b = [], x, y;
    for (x = 0; x < N; x++) {
      b[x] = [];
      for (y = 0; y < N; y++) b[x][y] = 0;
    }
    return b;
  }

  function cloneBoard(b) {
    var c = [], x;
    for (x = 0; x < N; x++) c[x] = b[x].slice();
    return c;
  }

  // 出界返回 3（既非空也非任何一方，天然堵头）
  function stoneAt(board, x, y) {
    if (x < 0 || y < 0 || x >= N || y >= N) return 3;
    return board[x][y];
  }

  function coordName(x, y) { return FILES.charAt(x) + (y + 1); }

  /* ============ 引擎：点评估 ============
   * 任何威胁最终都要通过「在某点落子」实现，所以只评估点本身：
   * 假定该点落下 color，数四方向以它为中心的连子数与两端封堵数，
   * 查分值表。跳型（XXX_X）不需要专门识别——对手真要下的是那个
   * 空位，在那个空位上这套计数自然数出 count=4。 */

  var SCORES = {
    FIVE: 10000000, LIVE_FOUR: 1000000, RUSH_FOUR: 100000, LIVE_THREE: 50000,
    SLEEP_THREE: 2000, LIVE_TWO: 600, SLEEP_TWO: 120, LIVE_ONE: 40
  };
  var KIND_ORDER = ['FIVE', 'LIVE_FOUR', 'RUSH_FOUR', 'LIVE_THREE', 'SLEEP_THREE', 'LIVE_TWO', 'SLEEP_TWO', 'LIVE_ONE'];
  var KIND_NAMES = {
    FIVE: '成五', LIVE_FOUR: '活四', RUSH_FOUR: '冲四', LIVE_THREE: '活三',
    SLEEP_THREE: '眠三', LIVE_TWO: '活二', SLEEP_TWO: '眠二', LIVE_ONE: '连一'
  };
  var DIRS = [[1, 0], [0, 1], [1, 1], [1, -1]];

  // (x,y) 落 color 后，单方向 (dx,dy) 的威胁档位名（null = 无档位）
  function dirInfo(board, x, y, dx, dy, color) {
    var count = 1, i, openA, openB, open;
    i = 1;
    while (stoneAt(board, x + dx * i, y + dy * i) === color) { count++; i++; }
    openA = stoneAt(board, x + dx * i, y + dy * i) === 0 ? 1 : 0;
    i = 1;
    while (stoneAt(board, x - dx * i, y - dy * i) === color) { count++; i++; }
    openB = stoneAt(board, x - dx * i, y - dy * i) === 0 ? 1 : 0;
    open = openA + openB;
    if (count >= 5) return 'FIVE';
    if (count === 4) return open === 2 ? 'LIVE_FOUR' : (open === 1 ? 'RUSH_FOUR' : null);
    if (count === 3) return open === 2 ? 'LIVE_THREE' : (open === 1 ? 'SLEEP_THREE' : null);
    if (count === 2) return open === 2 ? 'LIVE_TWO' : (open === 1 ? 'SLEEP_TWO' : null);
    return open > 0 ? 'LIVE_ONE' : null;
  }

  // (x,y) 落 color 后，四方向合计威胁分
  function evalPoint(board, x, y, color) {
    var total = 0, i, info, s;
    for (i = 0; i < 4; i++) {
      info = dirInfo(board, x, y, DIRS[i][0], DIRS[i][1], color);
      s = info ? SCORES[info] : 0;
      if (s) total += s;
    }
    return total;
  }

  // 四方向的最高档位名（讲解用）
  function topKind(board, x, y, color) {
    var best = null, i, info;
    for (i = 0; i < 4; i++) {
      info = dirInfo(board, x, y, DIRS[i][0], DIRS[i][1], color);
      if (info && (!best || KIND_ORDER.indexOf(info) < KIND_ORDER.indexOf(best))) best = info;
    }
    return best;
  }

  // 候选点：与任意已有棋子切比雪夫距离 ≤2 的空点；空盘返回中心
  function candidateMoves(board) {
    var list = [], x, y, dx, dy, v, near;
    for (x = 0; x < N; x++) {
      for (y = 0; y < N; y++) {
        if (board[x][y] !== 0) continue;
        near = false;
        for (dx = -2; dx <= 2 && !near; dx++) {
          for (dy = -2; dy <= 2; dy++) {
            if (!dx && !dy) continue;
            v = stoneAt(board, x + dx, y + dy);
            if (v === 1 || v === 2) { near = true; break; }
          }
        }
        if (near) list.push({ x: x, y: y });
      }
    }
    return list.length ? list : [{ x: 7, y: 7 }];
  }

  /* ============ 引擎：选点 ============ */

  function bestMove(board, color, level) {
    var opp = color === 1 ? 2 : 1;
    var lv = level || 2;
    var cands = candidateMoves(board);
    var scored = [], i, p, myGain, oppGain, w, s;

    for (i = 0; i < cands.length; i++) {
      p = cands[i];
      myGain = evalPoint(board, p.x, p.y, color);
      oppGain = evalPoint(board, p.x, p.y, opp);
      scored.push({ x: p.x, y: p.y, myGain: myGain, oppGain: oppGain });
    }

    // 终局短路：能成五直接下；对手能成五必须挡（挡点里挑综合最优）
    var winNow = null, mustBlock = [];
    for (i = 0; i < scored.length; i++) {
      if (scored[i].myGain >= SCORES.FIVE) { winNow = scored[i]; break; }
      if (scored[i].oppGain >= SCORES.FIVE) mustBlock.push(scored[i]);
    }
    if (winNow) return finish(board, winNow, color, lv);
    if (mustBlock.length) {
      mustBlock.sort(function (a, b) { return (b.myGain + b.oppGain) - (a.myGain + a.oppGain); });
      return finish(board, mustBlock[0], color, lv);
    }

    w = lv === 1 ? 0.6 : (lv === 3 ? 1.15 : 1.0);
    for (i = 0; i < scored.length; i++) {
      s = scored[i].myGain + scored[i].oppGain * w;
      if (lv === 1) s *= 0.94 + Math.random() * 0.12;   // 入门档 ±6% 抖动：会漏看威胁
      scored[i].s = s;
    }
    scored.sort(function (a, b) { return b.s - a.s; });

    // 进阶/挑战：一层「对手最佳应手」展望——我下 p 后对手能拿到的
    // 最好综合收益越大，p 越差；挑战档还看 myNext（我后续连子）
    var topK = lv === 3 ? 14 : (lv === 2 ? 8 : scored.length);
    var pool = scored.slice(0, Math.min(topK, scored.length));
    var best = null;
    for (i = 0; i < pool.length; i++) {
      pool[i].v = pool[i].s - lookahead(board, pool[i], color, opp, lv);
      if (!best || pool[i].v > best.v) best = pool[i];
    }
    return finish(board, best || scored[0], color, lv);
  }

  // p 落下后对手最强应手的综合收益（负向项）
  function lookahead(board, p, color, opp, lv) {
    var b2 = cloneBoard(board);
    b2[p.x][p.y] = color;
    var cands = candidateMoves(b2);
    var worst = 0, myNext = 0, i, q, v;
    for (i = 0; i < cands.length; i++) {
      q = cands[i];
      v = evalPoint(b2, q.x, q.y, opp) + evalPoint(b2, q.x, q.y, color) * 0.9;
      if (v > worst) worst = v;
    }
    if (lv === 3) {
      for (i = 0; i < cands.length; i++) {
        q = cands[i];
        v = evalPoint(b2, q.x, q.y, color);
        if (v > myNext) myNext = v;
      }
      worst -= myNext * 0.5;
    }
    return worst;
  }

  function finish(board, p, color, lv) {
    return {
      x: p.x, y: p.y,
      score: p.myGain,
      oppGain: p.oppGain,
      kind: topKind(board, p.x, p.y, color),
      kindName: KIND_NAMES[topKind(board, p.x, p.y, color)] || '',
      why: explain(board, p.x, p.y, color)
    };
  }

  /* ============ 引擎：胜负 ============ */

  // 从 (x,y) 四方向数连子，≥5 返回 { color, cells }（连线上所有子，用于高亮）
  function checkWin(board, x, y) {
    var color = board[x][y];
    if (!color) return null;
    var i, d, cells;
    for (i = 0; i < 4; i++) {
      d = DIRS[i];
      cells = collectLine(board, x, y, d[0], d[1], color);
      if (cells.length >= 5) return { color: color, cells: cells };
    }
    return null;
  }

  function collectLine(board, x, y, dx, dy, color) {
    var cells = [{ x: x, y: y }], i;
    for (i = 1; stoneAt(board, x + dx * i, y + dy * i) === color; i++) cells.push({ x: x + dx * i, y: y + dy * i });
    for (i = 1; stoneAt(board, x - dx * i, y - dy * i) === color; i++) cells.unshift({ x: x - dx * i, y: y - dy * i });
    return cells;
  }

  /* ============ 引擎：讲解（大白话） ============
   * explain(board, x, y, color)：站在 color 方视角解释「为什么下这」。
   * board 是落子前的局面（评注该手时它是「当时」的局面）。
   * 攻防合挑最高优先级的一句（对外口径就是一句大白话，见 FAQ）；全无威胁时给占位解释。 */

  function explain(board, x, y, color) {
    var opp = color === 1 ? 2 : 1;
    var my = topKind(board, x, y, color);
    var their = topKind(board, x, y, opp);
    var parts = [], centerDist = Math.abs(x - 7) + Math.abs(y - 7);

    // 进攻侧
    if (my === 'FIVE') parts.push('这一手连成五子，直接赢下这局');
    else if (my === 'LIVE_FOUR') parts.push('落成活四，两头都是空的，下一手必胜');
    else if (my === 'RUSH_FOUR') parts.push('冲四：逼对方应一手');
    else if (my === 'LIVE_THREE') parts.push('搭出活三，两头都通，威胁升级');
    // 防守侧（对手在这落子会形成的最高威胁）
    else if (their === 'FIVE') parts.push('不挡这里对手就要连成五了，必应');
    else if (their === 'LIVE_FOUR') parts.push('对手这就要成活四了，先堵一头');
    else if (their === 'RUSH_FOUR') parts.push('挡掉对手的冲四');
    else if (their === 'LIVE_THREE') parts.push('对手的活三再不管就成活四，先堵一头');

    if (!parts.length) {
      if (stoneCount(board) < 6 && centerDist <= 4) parts.push('没有直接威胁，先占中间，一颗子牵住几个方向');
      else if (my === 'SLEEP_THREE' || my === 'LIVE_TWO') parts.push('没有直接威胁，先连子成眠三或活二，为以后的威胁打底');
      else if (their === 'SLEEP_THREE' || their === 'LIVE_TWO') parts.push('顺手压一压对方这边的连子');
      else parts.push('这步没有明显的攻防点，占一个将来用得上的位置');
    }
    return parts.slice(0, 2);
  }

  function stoneCount(board) {
    var n = 0, x, y;
    for (x = 0; x < N; x++) for (y = 0; y < N; y++) if (board[x][y]) n++;
    return n;
  }

  /* ============ UI：挂载与状态 ============ */

  function E(id) { return document.getElementById(id); }
  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  var root = null;
  var ui = {};
  var S = {
    board: emptyBoard(),
    moves: [],            // [{x, y, color}]
    level: 1,
    soundOn: true,
    busy: false,
    replayTimer: null,    // 「回放这局」定时器（非 null = 回放中）
    ended: false,
    winner: 0,            // 1=你 2=对手 3=和棋
    resigned: false,
    reviewAt: -1,         // 回看指针（moves 下标）
    hintPt: null,
    winCells: null
  };
  var RECORD_KEY = STORE_KEY + '-record';

  // 前 k 手之后的局面（回看与讲解统一走它：内存只存一份当前 board）
  function boardAt(k) {
    var b = emptyBoard(), i;
    for (i = 0; i < k; i++) b[S.moves[i].x][S.moves[i].y] = S.moves[i].color;
    return b;
  }

  function buildBoard() {
    ui.board.textContent = '';
    ui.cells = [];
    for (var y = 0; y < N; y++) {
      for (var x = 0; x < N; x++) {
        (function (cx, cy) {
          var c = el('button', 'st-wz-cell');
          c.type = 'button';
          c.dataset.pos = coordName(cx, cy);
          c.setAttribute('aria-label', '第' + (cy + 1) + '行 ' + FILES.charAt(cx) + '列（' + coordName(cx, cy) + '）');
          c.appendChild(el('span', 'st-wz-stone'));
          c.addEventListener('click', function () { onCell(cx, cy); });
          ui.board.appendChild(c);
          ui.cells.push({ el: c, x: cx, y: cy });
        })(x, y);
      }
    }
  }

  function cellAt(x, y) { return ui.cells[y * N + x]; }

  function onCell(x, y) {
    stopReplay();   // 回放中点棋盘 = 停止回放并回到最新继续下
    if (S.reviewAt < S.moves.length - 1) {   // 回看态：点棋盘 = 回到最新继续下
      S.reviewAt = S.moves.length - 1;
      renderAll();
      return;
    }
    if (S.busy || S.ended || S.board[x][y] !== 0) return;
    placeMove(x, y, 1);
    if (!S.ended) aiMove();
  }

  function placeMove(x, y, color) {
    S.board[x][y] = color;
    S.moves.push({ x: x, y: y, color: color });
    S.hintPt = null;
    S.reviewAt = S.moves.length - 1;
    var win = checkWin(S.board, x, y);
    if (win) {
      S.ended = true;
      S.winner = color;
      S.winCells = win.cells;
      bumpRecord(color === 1 ? 'w' : 'l');
    } else if (S.moves.length === N * N) {
      S.ended = true;
      S.winner = 3;
      bumpRecord('d');
    }
    sfx(color, !!win);
    save();
    renderAll();
  }

  function aiMove() {
    S.busy = true;
    renderAll();
    setTimeout(function () {
      var mv = bestMove(S.board, 2, S.level);
      S.busy = false;
      placeMove(mv.x, mv.y, 2);
    }, 350 + Math.random() * 350);
  }

  /* ============ UI：渲染 ============ */

  function renderAll() {
    if (ui.thinking) ui.thinking.hidden = !S.busy;
    renderBoard();
    renderStatus();
    renderExplain();
    renderMoves();
    renderButtons();
  }

  function renderBoard() {
    var view = boardAt(S.reviewAt + 1);
    var i, c, stone;
    for (i = 0; i < ui.cells.length; i++) {
      c = ui.cells[i];
      stone = c.el.firstChild;
      var v = view[c.x][c.y];
      if (v && stone) {
        stone.className = 'st-wz-stone ' + (v === 1 ? 'st-wz-black' : 'st-wz-white');
        c.el.classList.add('is-filled');
      } else if (stone) {
        stone.className = 'st-wz-stone';
        c.el.classList.remove('is-filled');
      }
      var isLast = S.reviewAt >= 0 && S.moves[S.reviewAt] &&
        S.moves[S.reviewAt].x === c.x && S.moves[S.reviewAt].y === c.y;
      c.el.classList.toggle('is-last', !!isLast && !S.winCells);
      var isWin = false, k;
      if (S.winCells) {
        for (k = 0; k < S.winCells.length; k++) {
          if (S.winCells[k].x === c.x && S.winCells[k].y === c.y) { isWin = true; break; }
        }
      }
      c.el.classList.toggle('is-win', isWin);
      c.el.classList.toggle('is-hint', !!S.hintPt && S.hintPt.x === c.x && S.hintPt.y === c.y && S.reviewAt === S.moves.length - 1);
      if (S.ended || S.busy) c.el.disabled = true;
      else if (S.reviewAt < S.moves.length - 1) c.el.disabled = false; // 回看态可点（回最新）
      else c.el.disabled = !!v;
    }
  }

  function sideName(color) { return color === 1 ? '黑' : '白'; }

  function renderStatus() {
    var t = ui.status;
    if (S.ended) {
      if (S.winner === 3) t.textContent = '棋盘下满了，和棋。点「开始新的一局」再来。';
      else if (S.winner === 1) t.textContent = '五连！这局你赢了。想再来一局就点「开始新的一局」。';
      else if (S.resigned) t.textContent = '你认输了。想回到认输前就点「悔一步」，或者开一局新的。';
      else t.textContent = '对手连成五子，这局输了。用「悔一步」回到失误前，或点行子列表回看它是在哪连起来的。';
      t.textContent += ' ' + recordText();
      return;
    }
    if (S.busy) { t.textContent = '对手正在算这一步……（全部在本地算，没有请求服务器）'; return; }
    if (S.reviewAt < S.moves.length - 1) {
      t.textContent = S.replayTimer
        ? '回放中：第 ' + (S.reviewAt + 1) + ' / ' + S.moves.length + ' 手。再点「停止回放」停在这里。'
        : '正在回看第 ' + (S.reviewAt + 1) + ' / ' + S.moves.length + ' 手。点「下一步」回到最新，或直接点棋盘继续下。';
      return;
    }
    t.textContent = S.moves.length
      ? '轮到你了（执黑）· 已下 ' + S.moves.length + ' 手'
      : '点棋盘上的交叉点落子，你执黑先行。';
  }

  function renderExplain() {
    var box = ui.explain;
    box.textContent = '';
    if (S.reviewAt < 0) {
      var empty = el('p', 'st-explain-empty');
      empty.appendChild(el('span', '', '对手走完，这里会出现一句它为什么这么走'));
      box.appendChild(empty);
      return;
    }
    var m = S.moves[S.reviewAt];
    var why = explain(boardAt(S.reviewAt), m.x, m.y, m.color);
    var head = el('p', 'st-explain-head', '第 ' + (S.reviewAt + 1) + ' 手 · ' + sideName(m.color) + '子 · ' + coordName(m.x, m.y));
    var list = el('ul', 'st-explain-list');
    for (var i = 0; i < why.length; i++) {
      var li = el('li', '', why[i]);
      list.appendChild(li);
    }
    box.appendChild(head);
    box.appendChild(list);
  }

  function moveTitle(idx) {
    var m = S.moves[idx];
    var why = explain(boardAt(idx), m.x, m.y, m.color);
    return (m.color === 1 ? '你 · ' : '对手 · ') + coordName(m.x, m.y) + '：' + why.join('；');
  }

  // 行子表（对齐国象 st-move-row 结构）：每行一整回合，你（黑）| 电脑（白），
  // 电脑手 hover 的 title 就是它的理由；点任意一手回看
  function renderMoves() {
    var box = ui.moves;
    box.textContent = '';
    if (!S.moves.length) return;   // 空态占位由 .st-moves:empty::after 出
    var head = el('li', 'st-move-row st-move-row--head');
    head.appendChild(el('span', 'st-move-no', '#'));
    head.appendChild(el('span', 'st-move-col', '你（黑）'));
    head.appendChild(el('span', 'st-move-col', '电脑（白）'));
    box.appendChild(head);
    for (var i = 0; i < S.moves.length; i += 2) {
      var row = el('li', 'st-move-row');
      row.appendChild(el('span', 'st-move-no', (i / 2 + 1) + '.'));
      for (var k = 0; k < 2; k++) {
        var idx = i + k;
        if (idx >= S.moves.length) { row.appendChild(el('span', 'st-move-ply')); continue; }
        var m = S.moves[idx];
        var b = el('button', 'st-move-ply');
        b.type = 'button';
        b.textContent = coordName(m.x, m.y);
        b.setAttribute('data-ply', String(idx));
        if (S.reviewAt === idx) b.className += ' is-cur';
        b.title = moveTitle(idx);
        (function (ply) {
          b.addEventListener('click', function () {
            stopReplay();
            S.reviewAt = ply;
            S.hintPt = null;
            renderAll();
          });
        })(idx);
        row.appendChild(b);
      }
      box.appendChild(row);
    }
  }

  function renderButtons() {
    var reviewing = S.reviewAt < S.moves.length - 1;
    var replaying = !!S.replayTimer;
    var canUndo = S.moves.length >= 2 && !S.busy && !replaying && (!S.ended || S.winner === 2 || S.winner === 3 || S.resigned);
    ui.newBtn.disabled = false;
    ui.undoBtn.disabled = !canUndo;
    ui.hintBtn.disabled = S.busy || S.ended || reviewing || replaying;
    ui.resignBtn.disabled = S.busy || S.ended || replaying;
    ui.replayBtn.disabled = !S.moves.length || S.busy;
    ui.replayLabel.textContent = replaying ? '停止回放' : '回放这局';
    ui.prevBtn.disabled = S.reviewAt <= 0;
    ui.nextBtn.disabled = !reviewing;
    ui.exportBtn.disabled = !S.moves.length;
    ui.copyBtn.disabled = !S.moves.length;
    if (ui.reviewBtn) ui.reviewBtn.disabled = !S.moves.length || S.busy;
  }

  /* ============ 战绩与存档（全在本机 localStorage） ============ */

  function loadRecord() {
    try {
      var r = JSON.parse(localStorage.getItem(RECORD_KEY) || '{}');
      return { w: r.w || 0, l: r.l || 0, d: r.d || 0 };
    } catch (e) { return { w: 0, l: 0, d: 0 }; }
  }

  function bumpRecord(key) {
    var r = loadRecord();
    r[key]++;
    try { localStorage.setItem(RECORD_KEY, JSON.stringify(r)); } catch (e) { /* 隐私模式静默 */ }
  }

  function recordText() {
    var r = loadRecord();
    if (!r.w && !r.l && !r.d) return '';
    return '（本机战绩：' + r.w + ' 胜 ' + r.l + ' 负' + (r.d ? ' ' + r.d + ' 和' : '') + '，只存在这台设备）';
  }

  function save() {
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify({
        v: 1, level: S.level, soundOn: S.soundOn,
        moves: S.moves, resigned: S.resigned, winner: S.winner
      }));
    } catch (e) { /* 存储不可用静默（无痕模式） */ }
  }

  function restore() {
    try {
      var raw = localStorage.getItem(STORE_KEY);
      if (!raw) return false;
      var d = JSON.parse(raw);
      if (!d || d.v !== 1 || !d.moves || !d.moves.length) return false;
      S.level = d.level || 1;
      S.soundOn = d.soundOn !== false;
      S.moves = d.moves;
      S.resigned = !!d.resigned;
      S.winner = d.winner || 0;
      S.ended = !!S.winner;
      S.board = boardAt(S.moves.length);
      S.reviewAt = S.moves.length - 1;
      var last = S.moves[S.moves.length - 1];
      var win = last && !S.ended ? checkWin(S.board, last.x, last.y) : (S.ended && last ? checkWin(S.board, last.x, last.y) : null);
      S.winCells = win ? win.cells : null;
      return true;
    } catch (e) { return false; }
  }

  /* ============ 导出与复制 ============ */

  function resultLine() {
    if (!S.ended) return '未结束';
    if (S.winner === 3) return '和棋';
    if (S.winner === 1) return '黑胜（你）';
    return S.resigned ? '白胜（对手）——你认输' : '白胜（对手）';
  }

  // 纯函数（冒烟钩子直调）：棋谱文本 = 头部（规则/强度/结果/战绩）+ 每行一步坐标
  function exportLines(moves, level, result, rec) {
    var lines = [
      '学习乐园 · 五子棋棋谱',
      '导出时间：' + new Date().toISOString().slice(0, 10),
      '规则：无禁手自由规则（长连也算五连），黑先',
      '对手强度：' + levelName(level),
      '结果：' + result,
      '本机战绩：' + rec.w + ' 胜 ' + rec.l + ' 负 ' + rec.d + ' 和',
      ''
    ];
    for (var i = 0; i < moves.length; i++) {
      lines.push((i + 1) + '. ' + sideName(moves[i].color) + ' ' + coordName(moves[i].x, moves[i].y));
    }
    return lines;
  }

  function exportText() {
    var lines = exportLines(S.moves, S.level, resultLine(), loadRecord());
    var text = lines.join('\n') + '\n';
    var blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    var name = '学习乐园-五子棋-' + new Date().toISOString().slice(0, 10) + '.txt';
    deliverDownload(name, blob);
  }

  function deliverDownload(name, blob) {
    var usable = false;
    try { usable = !!(global.URL && global.URL.createObjectURL); } catch (e) { usable = false; }
    if (!usable) {
      // 无 createObjectURL 的环境（老浏览器/部分内嵌 webview）：留观测桩供冒烟取证
      (global.__wzWrites = global.__wzWrites || []).push({ name: name, blob: blob });
      return;
    }
    var url = global.URL.createObjectURL(blob);
    var a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    global.__wzLastDownload = name;   // 真链路观测点（jsdom 有 createObjectURL 时冒烟走这里）
    setTimeout(function () { global.URL.revokeObjectURL(url); }, 1000);
  }

  function copyText() {
    var lines = [];
    for (var i = 0; i < S.moves.length; i++) {
      lines.push((i + 1) + '. ' + sideName(S.moves[i].color) + ' ' + coordName(S.moves[i].x, S.moves[i].y));
    }
    var text = '学习乐园 · 五子棋（' + resultLine() + '）\n' + lines.join('\n');
    var ok = false;
    if (global.ToolUI && global.ToolUI.copy) ok = global.ToolUI.copy(text);
    if (!ok) {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { ok = document.execCommand('copy'); } catch (e) { ok = false; }
      ta.remove();
    }
    toast(ok ? '已复制棋谱文本' : '复制失败，请用「导出棋谱」存文件');
  }

  function toast(msg) {
    if (!ui.saved) return;
    ui.saved.textContent = msg;
    ui.saved.hidden = false;
    clearTimeout(toast._t);
    toast._t = setTimeout(function () { ui.saved.hidden = true; }, 2200);
  }

  /* 复盘移交：把本局棋谱文本放进 sessionStorage，跳到五子棋复盘页自动分析。
     文本与「复制棋谱」同格式（1. 黑 H8 一行一手），复盘页以步序定颜色、坐标为权威。
     sessionStorage 被禁时退回复制，用户手动粘贴一样能复盘。 */
  var HANDOFF_KEY = 'study-wuziqi-fupan-handoff';
  function handoffReview() {
    if (!S.moves.length || S.busy) return;
    var lines = [];
    for (var i = 0; i < S.moves.length; i++) {
      lines.push((i + 1) + '. ' + sideName(S.moves[i].color) + ' ' + coordName(S.moves[i].x, S.moves[i].y));
    }
    var text = '学习乐园 · 五子棋棋谱\n\n' + lines.join('\n');
    try {
      global.sessionStorage.setItem(HANDOFF_KEY, text);
      /* 跳转路径以模板注入的栏根为准（data-st-base），硬编码 /study 在子路径部署会 404 */
      var base = (root && root.getAttribute('data-st-base')) || '/study';
      global.location.href = base + '/wuziqi-fupan';
      return;
    } catch (e) { /* sessionStorage 被禁：退回复制 */ }
    copyText();
    toast('浏览器不允许中转，棋谱已复制——到复盘页粘贴即可');
  }

  function levelName(lv) { return lv === 1 ? '入门' : (lv === 2 ? '进阶' : '挑战'); }

  /* ============ 音效（WebAudio 本地合成，无音频文件） ============ */

  /* 手机外放响度补偿（口径统一，由 scripts/boost-sfx-mobile.mjs 批量写入）：
     手机小喇叭对 500Hz 以下钝感、超短音感知响度低——触屏设备把提示音增益翻倍、
     低于 500Hz 的音移高八度、衰减拉长；桌面/耳机维持原响度。 */
  var SFX_MOBILE = false;
  try {
    SFX_MOBILE = (global.matchMedia && global.matchMedia('(pointer: coarse)').matches) ||
      (global.navigator && global.navigator.maxTouchPoints > 0);
  } catch (e) { /* 检测不可用按桌面处理 */ }
  var actx = null;
  function sfx(color, win) {
    if (!S.soundOn) return;
    try {
      var AC = global.AudioContext || global.webkitAudioContext;
      if (!AC) return;
      if (!actx) actx = new AC();
      if (actx.state === 'suspended' && actx.resume) actx.resume();
      var t = actx.currentTime;
      var freqs = win ? [330, 415, 494] : [color === 1 ? 240 : 190];
      for (var i = 0; i < freqs.length; i++) {
        var o = actx.createOscillator();
        var g = actx.createGain();
        o.type = 'triangle';
        o.frequency.setValueAtTime(SFX_MOBILE && freqs[i] < 500 ? freqs[i] * 2 : freqs[i], t + i * 0.09);
        g.gain.setValueAtTime(0.0001, t + i * 0.09);
        g.gain.exponentialRampToValueAtTime(SFX_MOBILE ? 0.45 : 0.2, t + i * 0.09 + 0.008);
        g.gain.exponentialRampToValueAtTime(0.0001, t + i * 0.09 + (SFX_MOBILE ? 0.19 : 0.13));
        o.connect(g);
        g.connect(actx.destination);
        o.start(t + i * 0.09);
        o.stop(t + i * 0.09 + (SFX_MOBILE ? 0.24 : 0.15));
      }
    } catch (e) { /* 音频不可用静默 */ }
  }

  /* ============ 交互动作 ============ */

  /* ============ 回放这局（自动重走整局，播完停在最新） ============ */

  // 停止回放：只清定时器，不渲染（调用方各自 renderAll）
  function stopReplay() {
    if (!S.replayTimer) return;
    clearTimeout(S.replayTimer);
    S.replayTimer = null;
  }

  function replayStep() {
    if (S.reviewAt >= S.moves.length - 1) { S.replayTimer = null; renderAll(); return; }
    S.reviewAt++;
    var done = S.reviewAt >= S.moves.length - 1;
    if (done) S.replayTimer = null;   // 播完：先清定时器再渲染，按钮文案才随最后一手一起复原
    renderAll();
    if (!done) S.replayTimer = setTimeout(replayStep, 650);
  }

  // 回放中再点一次 = 停在当前手；平时点 = 从第 0 手自动重走整局
  function toggleReplay() {
    if (S.busy) return;
    if (S.replayTimer) { stopReplay(); renderAll(); return; }
    if (!S.moves.length) return;
    S.hintPt = null;
    S.reviewAt = -1;
    renderAll();
    S.replayTimer = setTimeout(replayStep, 400);
  }

  function newGame() {
    stopReplay();
    S.board = emptyBoard();
    S.moves = [];
    S.ended = false;
    S.winner = 0;
    S.resigned = false;
    S.reviewAt = -1;
    S.hintPt = null;
    S.winCells = null;
    S.busy = false;
    save();
    renderAll();
  }

  function undo() {
    if (S.busy) return;
    stopReplay();
    var n = S.moves.length % 2 === 0 ? 2 : 1;
    if (S.moves.length < n) return;
    S.moves.length -= n;
    S.board = boardAt(S.moves.length);
    S.ended = false;
    S.winner = 0;
    S.resigned = false;
    S.winCells = null;
    S.hintPt = null;
    S.reviewAt = S.moves.length - 1;
    save();
    renderAll();
  }

  function hint() {
    if (S.busy || S.ended || S.reviewAt < S.moves.length - 1) return;
    stopReplay();
    var mv = bestMove(S.board, 1, 2);   // 按进阶口径给提示
    S.hintPt = { x: mv.x, y: mv.y };
    renderAll();
    ui.status.textContent = '提示：' + coordName(mv.x, mv.y) + ' —— ' + mv.why.join('；') + '。不是唯一好棋，供参考。';
  }

  function resign() {
    if (S.ended || S.busy) return;
    stopReplay();
    S.ended = true;
    S.winner = 2;
    S.resigned = true;
    bumpRecord('l');
    save();
    renderAll();
  }

  /* ============ 强度与音效选择器 ============ */

  function bindPicker(container, attr, getVal, setVal) {
    var btns = container.querySelectorAll('[data-' + attr + ']');
    for (var i = 0; i < btns.length; i++) {
      (function (b) {
        b.addEventListener('click', function () {
          setVal(b.getAttribute('data-' + attr));
          for (var j = 0; j < btns.length; j++) {
            var on = btns[j] === b;
            btns[j].classList.toggle('is-active', on);
            btns[j].setAttribute('aria-checked', on ? 'true' : 'false');
          }
          save();
        });
      })(btns[i]);
    }
    // 恢复当前值
    for (var k = 0; k < btns.length; k++) {
      var on = getVal() === btns[k].getAttribute('data-' + attr);
      btns[k].classList.toggle('is-active', on);
      btns[k].setAttribute('aria-checked', on ? 'true' : 'false');
    }
  }

  /* ============ boot ============ */

  function boot() {
    root = document.querySelector('[data-st-game]');
    if (!root) return;
    ui.board = root.querySelector('[data-st-board]');
    ui.thinking = root.querySelector('[data-st-thinking]');
    ui.status = root.querySelector('[data-st-status]');
    ui.explain = root.querySelector('[data-st-explain]');
    ui.moves = root.querySelector('[data-st-moves]');
    ui.saved = root.querySelector('[data-st-saved]');
    ui.newBtn = root.querySelector('[data-st-new]');
    ui.replayBtn = root.querySelector('[data-st-replay]');
    ui.replayLabel = root.querySelector('[data-st-replay-label]');
    ui.undoBtn = root.querySelector('[data-st-undo]');
    ui.hintBtn = root.querySelector('[data-st-hint]');
    ui.resignBtn = root.querySelector('[data-st-resign]');
    ui.prevBtn = root.querySelector('[data-st-prev]');
    ui.nextBtn = root.querySelector('[data-st-next]');
    ui.exportBtn = root.querySelector('[data-st-export]');
    ui.copyBtn = root.querySelector('[data-st-copy]');
    ui.reviewBtn = root.querySelector('[data-st-review]');

    var had = restore();
    buildBoard();

    bindPicker(root.querySelector('[data-st-level-picker]') || root, 'st-level',
      function () { return String(S.level); },
      function (v) { S.level = parseInt(v, 10) || 1; });
    bindPicker(root.querySelector('[data-st-sound-picker]') || root, 'st-sound',
      function () { return S.soundOn ? 'on' : 'off'; },
      function (v) { S.soundOn = v === 'on'; });

    ui.newBtn.addEventListener('click', newGame);
    ui.replayBtn.addEventListener('click', toggleReplay);
    ui.undoBtn.addEventListener('click', undo);
    ui.hintBtn.addEventListener('click', hint);
    ui.resignBtn.addEventListener('click', resign);
    ui.prevBtn.addEventListener('click', function () { stopReplay(); if (S.reviewAt > 0) { S.reviewAt--; S.hintPt = null; renderAll(); } });
    ui.nextBtn.addEventListener('click', function () { stopReplay(); if (S.reviewAt < S.moves.length - 1) { S.reviewAt++; renderAll(); } });
    ui.exportBtn.addEventListener('click', exportText);
    ui.copyBtn.addEventListener('click', copyText);
    if (ui.reviewBtn) ui.reviewBtn.addEventListener('click', handoffReview);

    renderAll();
    if (had) toast('已恢复上次没下完的这局');
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
  }

  /* ============ 冒烟与复用钩子 ============ */

  global.STWuziqi = {
    N: N,
    FILES: FILES,
    emptyBoard: emptyBoard,
    stoneAt: stoneAt,
    coordName: coordName,
    dirInfo: dirInfo,
    evalPoint: evalPoint,
    topKind: topKind,
    candidateMoves: candidateMoves,
    bestMove: bestMove,
    checkWin: checkWin,
    explain: explain,
    exportLines: exportLines,
    SCORES: SCORES,
    KIND_NAMES: KIND_NAMES
  };
})(typeof window !== 'undefined' ? window : globalThis);

